// Schmaler fetch-Client. Im Dev: /api → vite-proxy → http://localhost:8787.
// In Produktion: VITE_API_URL zeigt auf die Worker-URL.
//
// Auth-Strategie: Cookie (klassisch) + Authorization-Header (Fallback).
// Der Header wird bevorzugt, wenn ein Token im localStorage steht — nötig
// für iOS Safari + Inkognito, wo Cross-Site-Cookies blockiert sind.

const BASE = (import.meta as any).env?.VITE_API_URL ?? '/api';
const TOKEN_KEY = 'mvg_token';

export function getToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* localStorage disabled */ }
}

/** Vollständige API-URL für direkten Browser-Aufruf (window.open o.ä.). */
export function apiUrl(path: string): string {
  return `${BASE}${path}`;
}

export class ApiError extends Error {
  constructor(public status: number, public payload: any) {
    super(`API ${status}: ${payload?.error ?? 'error'}`);
  }
}

function authHeaders(): Record<string, string> {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(res.status, data);
  return data as T;
}

export const api = {
  get: <T,>(p: string) => request<T>(p),
  post: <T,>(p: string, body?: unknown) =>
    request<T>(p, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T,>(p: string, body: unknown) =>
    request<T>(p, { method: 'PATCH', body: JSON.stringify(body) }),
  del: <T,>(p: string) => request<T>(p, { method: 'DELETE' }),
};

/** Trigger a file download via XHR-then-Blob — schickt Cookie + Bearer-Token mit. */
export async function downloadFile(path: string, fallbackFilename: string): Promise<void> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw new ApiError(res.status, await res.json().catch(() => ({})));
  }
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  const filename = match?.[1] ?? fallbackFilename;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * HTML-Response holen (mit Auth) und in einem neuen Tab öffnen.
 * Nötig für Print-Seiten wie /members/:id/print, weil window.open() keine
 * Auth-Header senden kann und der neue Tab in Cross-Origin-Setups kein Cookie hat.
 */
export async function openAuthedHtml(path: string): Promise<void> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw new ApiError(res.status, await res.json().catch(() => ({})));
  }
  const html = await res.text();
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  // URL nach einiger Zeit freigeben (nachdem der neue Tab geladen hat)
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
